import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { Sparkles } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Create account — PixelEmpire AI",
    description: "Register a free PixelEmpire AI account to claim your permanent advertising block on the 100×100 grid.",
    path: "/register",
    noindex: true,
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register(email, password, name);
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="hero-orb bg-cyan-400" style={{ width: 500, height: 500, top: -120, right: -100 }} />
      <main className="pt-40 pb-24 px-4 max-w-md mx-auto">
        <div className="glass-strong p-8" data-testid="register-form">
          <div className="pill"><span>Join PixelEmpire</span></div>
          <h1 className="mt-3 font-display text-4xl">Create account</h1>
          <p className="mt-2 text-sm text-[var(--fg-muted)]">
            One account for buying, editing and analytics.
          </p>

          <div className="mt-6">
            <GoogleLoginButton label="Sign up with Google" testid="register-google" />
          </div>

          <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-widest text-[var(--fg-muted)]">
            <span className="flex-1 h-px bg-white/10" /> or <span className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="mt-4 space-y-3">
            <input data-testid="register-name" placeholder="Business or display name" value={name} onChange={(e) => setName(e.target.value)} className="input-lux" />
            <input data-testid="register-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-lux" />
            <input data-testid="register-password" type="password" required minLength={6} placeholder="Password (min 6 chars)" value={password} onChange={(e) => setPassword(e.target.value)} className="input-lux" />
            {error && <div className="text-sm text-red-500" data-testid="register-error">{error}</div>}
            <button data-testid="register-submit" className="btn-primary w-full" disabled={busy}>
              <Sparkles className="w-4 h-4" />{busy ? "Creating…" : "Create account"}
            </button>
          </form>
          <div className="mt-6 text-sm text-center text-[var(--fg-muted)]">
            Already registered? <Link to="/login" className="underline">Sign in</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
